/**
 * @file API functions for appointment type follow-up notifications
 */

import { ENV } from '../../../config/runtimeEnv';
import { createServiceClient } from '../../../lib/http/client';

const API_BASE_URL = `${ENV.VITE_HEALTHCARE_SERVER}/practitioner`;

export interface AppointmentTypeFollowUp {
	id: number;
	appointment_type_id: number;
	notification_template_id: number;
	delay_hours?: number;
	delay_days?: number;
	is_active: boolean;
	sort_order: number;
	created_at: string;
	updated_at: string;
}

export interface CreateFollowUpRequest {
	notification_template_id: number;
	delay_hours?: number;
	delay_days?: number;
	is_active?: boolean;
	sort_order?: number;
}

export interface UpdateFollowUpRequest {
	notification_template_id?: number;
	delay_hours?: number;
	delay_days?: number;
	is_active?: boolean;
	sort_order?: number;
}

export interface ReorderFollowUpRequest {
	follow_ups: Array<{
		id: number;
		sort_order: number;
	}>;
}

const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

export const fetchAppointmentTypeFollowUps = async (appointmentTypeId: number): Promise<AppointmentTypeFollowUp[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get(`/appointment-types/${appointmentTypeId}/follow-ups`);
		return response.data.data || [];
	} catch (error) {
		console.error('Error fetching follow-ups:', error);
		throw error;
	}
};

export const createAppointmentTypeFollowUp = async (
	appointmentTypeId: number,
	data: CreateFollowUpRequest
): Promise<AppointmentTypeFollowUp> => {
	try {
		const api = createApiInstance();
		const response = await api.post(`/appointment-types/${appointmentTypeId}/follow-ups`, data);
		return response.data.data;
	} catch (error) {
		console.error('Error creating follow-up:', error);
		throw error;
	}
};

export const updateAppointmentTypeFollowUp = async (
	appointmentTypeId: number,
	followUpId: number,
	data: UpdateFollowUpRequest
): Promise<AppointmentTypeFollowUp> => {
	try {
		const api = createApiInstance();
		const response = await api.put(`/appointment-types/${appointmentTypeId}/follow-ups/${followUpId}`, data);
		return response.data.data;
	} catch (error) {
		console.error('Error updating follow-up:', error);
		throw error;
	}
};

export const deleteAppointmentTypeFollowUp = async (
	appointmentTypeId: number,
	followUpId: number
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.delete(`/appointment-types/${appointmentTypeId}/follow-ups/${followUpId}`);
	} catch (error) {
		console.error('Error deleting follow-up:', error);
		throw error;
	}
};

export const reorderAppointmentTypeFollowUps = async (
	appointmentTypeId: number,
	data: ReorderFollowUpRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/appointment-types/${appointmentTypeId}/follow-ups/reorder`, data);
	} catch (error) {
		console.error('Error reordering follow-ups:', error);
		throw error;
	}
};

export const toggleAppointmentTypeFollowUpActive = async (
	appointmentTypeId: number,
	followUpId: number
): Promise<AppointmentTypeFollowUp> => {
	try {
		const api = createApiInstance();
		const response = await api.patch(`/appointment-types/${appointmentTypeId}/follow-ups/${followUpId}/toggle-active`);
		return response.data.data;
	} catch (error) {
		console.error('Error toggling follow-up active status:', error);
		throw error;
	}
};

export const fetchAvailableFollowUpTemplates = async (): Promise<any[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get('/follow-up-templates');
		const data = response.data;
		return Array.isArray(data) ? data : (data?.data ?? []);
	} catch (error) {
		console.error('Error fetching available follow-up templates:', error);
		throw error;
	}
};